import { v4 as uuidv4 } from "uuid";
import type { Tables } from "@/lib/supabase/supabaseTypes";
import type {
  Exercise,
  ExerciseSet,
  Workout,
} from "@/components/workouts/workoutsColumns";
import type { AiExerciseSet, AiToolCall } from "./types";

const buildSets = (rawSets: AiExerciseSet[] | undefined): ExerciseSet[] =>
  (rawSets ?? []).map((set) => ({
    id: uuidv4(),
    startRange: set.startRange,
    endRange: set.endRange,
    targetWeight: set.targetWeight,
    targetTimeSeconds: set.targetTimeSeconds,
    targetDistanceMeters: set.targetDistanceMeters,
  }));

const defaultSets = (): ExerciseSet[] => [
  { id: uuidv4(), startRange: 10, endRange: 12 },
];

const reindex = (groups: Workout["exerciseCollection"]) =>
  groups.map((group, exOrder) =>
    group.map((exercise, subOrder) => ({
      ...exercise,
      ex_order: exOrder,
      sub_order: subOrder,
    })),
  );

const exerciseFromLibrary = (
  exercise: Tables<"exercises">,
  config: {
    sets: ExerciseSet[];
    tempo?: string;
    rir?: number;
    rest_time?: number;
    coach_note?: string;
  },
  placement: { exOrder: number; sectionId: string | null },
): Exercise => ({
  ...exercise,
  sets: config.sets.length > 0 ? config.sets : defaultSets(),
  rest_time: config.rest_time ?? null,
  rir: config.rir ?? null,
  tempo: config.tempo ?? "",
  coach_note: config.coach_note ?? null,
  ex_order: placement.exOrder,
  sub_order: 0,
  sortableId: uuidv4(),
  section_id: placement.sectionId,
});

export const applyWorkoutTool = (
  workout: Workout,
  call: AiToolCall,
  findExercise: (id: string) => Tables<"exercises"> | undefined,
): Workout => {
  if (call.name === "no_workout_change") return workout;

  const sectionId = workout.sections.at(-1)?.id ?? null;

  if (call.name === "add_exercise") {
    const exercise = findExercise(call.args.exercise_id);
    if (!exercise) return workout;
    const next = exerciseFromLibrary(
      exercise,
      {
        sets: buildSets(call.args.sets),
        tempo: call.args.tempo,
        rir: call.args.rir,
        rest_time: call.args.rest_time,
        coach_note: call.args.coach_note,
      },
      {
        exOrder: workout.exerciseCollection.length,
        sectionId,
      },
    );
    return {
      ...workout,
      exerciseCollection: [...workout.exerciseCollection, [next]],
    };
  }

  if (call.name === "remove_exercise") {
    const exOrder = call.args.ex_order;
    if (exOrder < 0 || exOrder >= workout.exerciseCollection.length) {
      return workout;
    }
    const next = workout.exerciseCollection.filter((_, index) => index !== exOrder);
    return {
      ...workout,
      exerciseCollection: reindex(next),
    };
  }

  if (call.name === "replace_exercise") {
    const exercise = findExercise(call.args.exercise_id);
    const exOrder = call.args.ex_order;
    if (!exercise || exOrder < 0 || exOrder >= workout.exerciseCollection.length) {
      return workout;
    }
    const existingSection =
      workout.exerciseCollection[exOrder]?.[0]?.section_id ?? sectionId;
    const replacement = exerciseFromLibrary(
      exercise,
      {
        sets: buildSets(call.args.sets),
        tempo: call.args.tempo,
        rir: call.args.rir,
        rest_time: call.args.rest_time,
        coach_note: call.args.coach_note,
      },
      { exOrder, sectionId: existingSection },
    );
    const next = workout.exerciseCollection.map((group, index) =>
      index === exOrder ? [replacement] : group,
    );
    return { ...workout, exerciseCollection: next };
  }

  if (call.name === "update_exercise") {
    const exOrder = call.args.ex_order;
    if (exOrder < 0 || exOrder >= workout.exerciseCollection.length) {
      return workout;
    }
    const next = workout.exerciseCollection.map((group, index) => {
      if (index !== exOrder) return group;
      return group.map((row) => {
        const updated = { ...row };
        if (call.args.sets && call.args.sets.length > 0) {
          updated.sets = buildSets(call.args.sets);
        }
        if (call.args.tempo !== undefined) updated.tempo = call.args.tempo;
        if (call.args.rir !== undefined) updated.rir = call.args.rir;
        if (call.args.rest_time !== undefined) {
          updated.rest_time = call.args.rest_time;
        }
        if (call.args.coach_note !== undefined) {
          updated.coach_note = call.args.coach_note;
        }
        return updated;
      });
    });
    return { ...workout, exerciseCollection: next };
  }

  return workout;
};
